import MenuIcon from '@material-symbols/svg-300/rounded/menu.svg'
import React, { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Button from '../../components/basics/Button/Button'
import ConfirmDialog from '../../components/basics/ConfirmDialog/ConfirmDialog'
import Menu from '../../components/basics/Menu/Menu'
import ProgressBar from '../../components/basics/ProgressBar/ProgressBar'
import LearningItemImage from '../../components/LearningItemImage/LearningItemImage'
import UserSlug from '../../components/slugs/UserSlug/UserSlug'
import { LearningItem } from '../../models/LearningItem'
import { UserLearningAssignment } from '../../models/UserLearningAssignment'
import bookingForLearningItem from '../../state/calculations/bookingForLearningItem'
import learningItemTimeInvested from '../../state/calculations/learningItemTimeInvested'
import unfinishedLearningItems from '../../state/calculations/unfinishedLearningItems'
import {
  managerCanAssignLearningItemsToGroupMember,
  userCanCancelBooking,
  managerCanCancelBookingOfGroupMember,
  userCanEditUserLearningAssignment,
  userCanMarkLearningItemAsFinished,
  userCanRemoveLearningAssignment,
  userCanViewPrivateUserInformation,
  userCanAssignLearningItems,
  managerCanEditLearningAssignmentOfGroupMember,
  managerCanMarkLearningAssignmentAsFinishedForGroupMember,
  managerCanEditLearningAssignmentOfGroupMemberWhenAssignmentWasAddedDirectly,
  managerCanRemoveLearningAssignmentFromGroupMemberWhenItWasAddedDirectlyByGroupManager,
  managerCanCancelBookingOfGroupMemberWhenAssignmentWasAddedDirectly,
  managerCanMarkLearningAssignmentAsFinishedForGroupMemberWhenAssignmentWasAddedDirectly,
} from '../../state/calculations/permissions/userCan'
import { useLearningItem, useUser, useUsers } from '../../state/entities'
import useCancelScheduledCourseEventBooking from '../../state/mutations/useCancelScheduledCourseEventBooking'
import useRemoveLearningAssignmentsFromUsers from '../../state/mutations/useRemoveLearningAssignmentsFromUsers'
import useUserLearningItems from '../../state/queries/useUserLearningItems'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import useDialog from '../../utilities/useDialog'

import styles from './ManageUserPage.module.scss'
import useUserBookedLearningItems from '../../state/queries/useUserBookedLearningItems'
import useInvalidate from '../../state/useInvalidate'
import CourseAssignments from '../../components/CourseAssignments/CourseAssignments'

/**********************************************************************
 * ConfirmRemoveLearningAssignmentDialog
 **********************************************************************/
interface ConfirmRemoveLearningAssignmentDialogProps {
  userUuid: string
  assignersUuids: string[]
  learningItemUuid: string
  close: (result: { confirmed: boolean; selectedAssigningUserUuid?: string }) => void
}
const ConfirmRemoveLearningAssignmentDialog = React.memo(function ConfirmRemoveLearningAssignmentDialog({
  userUuid,
  assignersUuids,
  learningItemUuid,
  close,
}: ConfirmRemoveLearningAssignmentDialogProps) {
  const { t } = useTranslation()
  const { user } = useUser(userUuid)
  const { users: assigners } = useUsers(assignersUuids)
  const { learningItem } = useLearningItem(learningItemUuid)

  const [selectedAssigningUserUuid, setSelectedAssigningUserUuid] = useState<string>('')

  const handleClose = (confirmed: boolean) => {
    close({ confirmed, selectedAssigningUserUuid })
  }

  if (assignersUuids.length > 1) {
    return (
      <ConfirmDialog close={handleClose} yesLabel={t('generic cta.remove')} noLabel={t('generic cta.cancel')}>
        <div>
          <Trans t={t} i18nKey="manage learning.select which learning assignment be removed from user">
            <UserSlug user={user!} />
            {{ title: learningItem.title }}
          </Trans>
          {assigners.map((assigningUser) => (
            <div key={assigningUser.uuid} className={styles.assignersListItem}>
              <input
                type="radio"
                name="assigningUser"
                value={assigningUser.uuid}
                checked={selectedAssigningUserUuid === assigningUser.uuid}
                onChange={() => setSelectedAssigningUserUuid(assigningUser.uuid)}
              />
              <UserSlug user={assigningUser} />
            </div>
          ))}
        </div>
      </ConfirmDialog>
    )
  }

  const handleCloseOneOption = (confirmed: boolean) => {
    close({ confirmed, selectedAssigningUserUuid: assignersUuids[0] })
  }

  return (
    <ConfirmDialog close={handleCloseOneOption} yesLabel={t('generic cta.remove')} noLabel={t('generic cta.cancel')}>
      <div>
        <Trans t={t} i18nKey="manage learning.should learning assignment be removed from user">
          <UserSlug user={user!} />
          <UserSlug user={assigners[0]!} />
          {{ title: learningItem.title }}
        </Trans>
      </div>
    </ConfirmDialog>
  )
})

/**********************************************************************
 * ConfirmCancelBookingDialog
 **********************************************************************/
export interface ConfirmCancelBookingDialogProps {
  userUuid: string
  learningItemUuid: string
  close: (result: boolean) => void
}

export const ConfirmCancelBookingDialog = React.memo(function ConfirmCancelBookingDialog({
  userUuid,
  learningItemUuid,
  close,
}: ConfirmCancelBookingDialogProps) {
  const { t } = useTranslation()

  const { user } = useUser(userUuid)
  const { learningItem } = useLearningItem(learningItemUuid)

  return (
    <ConfirmDialog close={close} yesLabel={t('generic cta.remove')} noLabel={t('generic cta.cancel')}>
      <div>
        <Trans t={t} i18nKey="manage learning.should booking be cancelled for user">
          <UserSlug user={user} />
          {{
            title: learningItem.title,
          }}
        </Trans>
      </div>
    </ConfirmDialog>
  )
})

/**********************************************************************
 * Activity
 **********************************************************************/
const Activity = React.memo(function Activity({ learningItem }: { learningItem: LearningItem }) {
  const { t } = useTranslation()

  if (learningItem.kind === 'onDemandCourse') {
    if (learningItem.learningProgress && learningItem.learningProgress.firstAccessedAt) {
      return (
        <div className={styles.onlyTime}>
          <span>
            {t('{duration} spent', {
              duration: { seconds: learningItemTimeInvested(learningItem) },
            })}
          </span>
          <ProgressBar circular progress={learningItem.learningProgress.completionPercentage || 0} />
        </div>
      )
    }

    if (learningItem.onDemandCourse.booking) {
      return (
        <div>
          <span>{t('learning item.booked')}</span>
        </div>
      )
    }
  }

  if (learningItem.kind === 'scheduledCourse') {
    const booking = bookingForLearningItem(learningItem)
    const event = learningItem.scheduledCourse.events.find(({ uuid }) => uuid === booking?.scheduledCourseEventUuid)

    if (booking && event) {
      return (
        <div className={styles.dates}>
          <span>
            {t('learning item.booked')}{' '}
            {t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}
          </span>
          {learningItem.learningProgress && learningItem.learningProgress.firstAccessedAt && (
            <div className={styles.dateProgress}>
              <span>
                {t('{duration} spent', {
                  duration: { seconds: learningItemTimeInvested(learningItem) },
                })}
              </span>
              <ProgressBar circular progress={learningItem.learningProgress.completionPercentage || 0} />
            </div>
          )}
        </div>
      )
    }
  }

  return <div>-</div>
})

/**********************************************************************
 * UnfinishedLearningItem
 **********************************************************************/
export const UnfinishedLearningItem = React.memo(function UnfinishedLearningItem({
  userUuid,
  learningItem,
  assignment,
  isBooked,
  bookedEventUuid,
  hasAssignmentsFromOtherGroupsThanFromLoggedManager,
  isAssignedByUser,
}: {
  userUuid: string
  learningItem: LearningItem
  assignment?: UserLearningAssignment
  isBooked: boolean
  bookedEventUuid?: string
  hasAssignmentsFromOtherGroupsThanFromLoggedManager: boolean
  isAssignedByUser: boolean
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const showDialog = useDialog()
  const { user: currentUser } = useSession()

  const { user } = useUser(userUuid)

  const invalidate = useInvalidate()

  const actionMenuItems = []

  // MENU ITEM: Edit learning assignment
  if (
    assignment &&
    (userCanEditUserLearningAssignment(currentUser) ||
      managerCanEditLearningAssignmentOfGroupMember(
        currentUser?.managedGroups || [],
        user.groups || [],
        learningItem.groupLearningAssignments || []
      ) ||
      managerCanEditLearningAssignmentOfGroupMemberWhenAssignmentWasAddedDirectly(
        currentUser?.managedGroups || [],
        user.groups || [],
        assignment.assignedByUserUuid === currentUser?.uuid
      ))
  ) {
    actionMenuItems.push(
      <li key="edit">
        <Button
          component={Link}
          to={routes.editUserLearningAssignment.url({
            uuid: userUuid,
            learningItemUuid: learningItem.uuid,
          })}
          preventScrollReset
          menuItem
        >
          {t('generic cta.edit')}
        </Button>
      </li>
    )
  }

  // Remove learning assignment
  const removeLearningAssignmentsFromUsers = useRemoveLearningAssignmentsFromUsers()
  const handleRemoveLearningAssignment = useCallback(
    async (assignersUuids: string[]) => {
      const result = await showDialog(
        (close) => (
          <ConfirmRemoveLearningAssignmentDialog
            // @ts-ignore
            close={close}
            userUuid={userUuid}
            assignersUuids={assignersUuids}
            learningItemUuid={learningItem.uuid}
          />
        ),
        () => false
      )

      // @ts-ignore
      const { confirmed, selectedAssigningUserUuid } = result

      if (confirmed && selectedAssigningUserUuid) {
        removeLearningAssignmentsFromUsers.mutate({
          learningItemUuids: [learningItem.uuid],
          userUuids: [userUuid],
          assigningUserUuid: selectedAssigningUserUuid,
        })
      }
    },
    [removeLearningAssignmentsFromUsers, userUuid, learningItem]
  )

  // MENU ITEM: Remove learning assignment
  if (assignment && isAssignedByUser) {
    let assignersUuids = getAssignersUuidsOfLearningItemAssignments(learningItem)

    const canRemoveAnyAssignment = userCanRemoveLearningAssignment(currentUser)

    if (!canRemoveAnyAssignment && !!currentUser) {
      const canRemove = managerCanRemoveLearningAssignmentFromGroupMemberWhenItWasAddedDirectlyByGroupManager(
        currentUser.uuid,
        currentUser.managedGroups || [],
        user.groups || [],
        learningItem
      )

      assignersUuids = canRemove ? [currentUser.uuid] : []
    }

    if (assignersUuids.length > 0) {
      actionMenuItems.push(
        <li key="remove-assignment">
          <Button onClick={() => handleRemoveLearningAssignment(assignersUuids)} menuItem>
            {t('manage learning.cta remove learning assignment')}
          </Button>
        </li>
      )
    }
  }

  const cancelScheduledCourseEventBooking = useCancelScheduledCourseEventBooking()

  const handleCancelBooking = useCallback(async () => {
    const confirmed = await showDialog(
      (close) => <ConfirmCancelBookingDialog close={close} userUuid={userUuid} learningItemUuid={learningItem.uuid} />,
      () => false
    )
    if (confirmed) {
      cancelScheduledCourseEventBooking.mutate({ eventUuid: bookedEventUuid!, userUuid })

      invalidate.courses()
      invalidate.useUserBookedLearningItems()
    }
  }, [cancelScheduledCourseEventBooking, userUuid, bookedEventUuid])

  // MENU ITEM: cancel booking
  if (
    learningItem.kind === 'scheduledCourse' &&
    isBooked &&
    (userCanCancelBooking(currentUser) ||
      (!hasAssignmentsFromOtherGroupsThanFromLoggedManager &&
        (managerCanCancelBookingOfGroupMember(
          currentUser?.managedGroups || [],
          user.groups || [],
          learningItem.groupLearningAssignments || []
        ) ||
          managerCanCancelBookingOfGroupMemberWhenAssignmentWasAddedDirectly(
            currentUser?.managedGroups || [],
            user.groups || [],
            !!assignment && assignment.assignedByUserUuid === currentUser?.uuid
          ))))
  ) {
    actionMenuItems.push(
      <li key="cancel-booking">
        <Button onClick={handleCancelBooking} menuItem>
          {t('manage learning.cta cancel booking')}
        </Button>
      </li>
    )
  }

  // MENU ITEM: marked as finished
  if (
    assignment &&
    (userCanMarkLearningItemAsFinished(currentUser) ||
      managerCanMarkLearningAssignmentAsFinishedForGroupMember(
        currentUser?.managedGroups || [],
        user.groups || [],
        learningItem.groupLearningAssignments || []
      ) ||
      managerCanMarkLearningAssignmentAsFinishedForGroupMemberWhenAssignmentWasAddedDirectly(
        currentUser?.managedGroups || [],
        user.groups || [],
        assignment.assignedByUserUuid === currentUser?.uuid
      ))
  ) {
    actionMenuItems.push(
      <li key="mark-as-finished">
        <Button
          component={Link}
          to={routes.markLearningItemAsFinished.url({
            uuid: userUuid,
            learningItemUuid: learningItem.uuid,
          })}
          preventScrollReset
          menuItem
        >
          {t('manage learning.mark as finished.cta mark as finished')}
        </Button>
      </li>
    )
  }

  return (
    <section>
      <div className={styles.information}>
        <header>
          <h1>
            <Link to={routes.course.url({ uuid: learningItem.uuid })}>{learningItem.title}</Link>
          </h1>
          <span>{t(`learning item.type.${learningItem.type}`)}</span>
        </header>

        <section className={styles.assignment}>
          {learningItem.userLearningAssignments && learningItem.userLearningAssignments?.length > 0 && (
            <>
              <h1>{t('assignment')}</h1>
              <CourseAssignments learningItem={learningItem} />
            </>
          )}
        </section>

        <section className={styles.activity}>
          <h1>{t('activity')}</h1>
          <Activity learningItem={learningItem} />
        </section>
      </div>

      <div className={styles.imageAndDueDate}>
        <Link to={routes.course.url({ uuid: learningItem.uuid })}>
          <LearningItemImage learningItem={learningItem} className={styles.coverImage} />
        </Link>
        <div>{assignment?.dueDate && <span>{t('learning item.due date is', { date: assignment.dueDate })}</span>}</div>
      </div>

      {actionMenuItems.length > 0 && (
        <Menu small label={<MenuIcon />} className={styles.actions}>
          {actionMenuItems}
        </Menu>
      )}
    </section>
  )
})

export const getLearningItemUuids = (unfinishedItems: { learningItem: LearningItem }[]) => {
  return unfinishedItems.map(({ learningItem }) => learningItem.uuid)
}

/**********************************************************************
 * ManageUserPageLearningAssignments
 **********************************************************************/
export default React.memo(function ManageUserPageLearningAssignments({ uuid }: { uuid: string }) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  const { user } = useUser(uuid)

  const { userLearningItems } = useUserLearningItems(uuid)

  const unfinishedItems = useMemo(() => unfinishedLearningItems(userLearningItems, uuid), [userLearningItems, uuid])

  const unfinishedLearningItemUuids = getLearningItemUuids(unfinishedItems)

  const { userBookedLearningItems } = useUserBookedLearningItems(uuid, unfinishedLearningItemUuids, currentUser?.uuid)

  if (!uuid || !user) {
    throw new Error('User not found')
  }

  if (!userCanViewPrivateUserInformation(currentUser, user)) {
    return null
  }

  return (
    <section className={styles.ManageUserPageLearningAssignments}>
      <header>
        <h1>{t('learning assignments')}</h1>
        {(userCanAssignLearningItems(currentUser) ||
          managerCanAssignLearningItemsToGroupMember(currentUser?.managedGroups || [], user.groups || [])) && (
          <Button
            small
            component={Link}
            to={routes.addLearningItemsToUser.url({ uuid, pathsOrCourses: '0' })}
            preventScrollReset
          >
            {t('manage learning.cta assign learning item')}
          </Button>
        )}
      </header>

      <main>
        {unfinishedItems.map(({ learningItem, assignment }) => (
          <UnfinishedLearningItem
            key={learningItem.uuid}
            userUuid={uuid}
            learningItem={learningItem}
            assignment={assignment as any}
            isBooked={userBookedLearningItems.some((item) => item.learningItemUuid === learningItem.uuid)}
            hasAssignmentsFromOtherGroupsThanFromLoggedManager={hasAssignmentsFromOtherGroupsThanFromLoggedManager(
              userBookedLearningItems,
              learningItem.uuid
            )}
            bookedEventUuid={getBookedEventUuid(userBookedLearningItems, learningItem.uuid)}
            isAssignedByUser={isAssignedByUser(learningItem)}
          />
        ))}
        {!unfinishedItems.length && <span className={styles.empty}>{t('no learning assignments found')}</span>}
      </main>
    </section>
  )
})

export function isAssignedByUser(learningItem: LearningItem): boolean {
  return (learningItem.userLearningAssignments ?? []).some((a) => a.assignedByUserUuid !== null)
}

export function hasAssignmentsFromOtherGroupsThanFromLoggedManager(
  userBookedLearningItems: { learningItemUuid: string; hasOtherGroupsAssignmentsThanFromProvidedManager: boolean }[],
  learningItemUuid: string
): boolean {
  return userBookedLearningItems.some(
    (item) => item.learningItemUuid === learningItemUuid && item.hasOtherGroupsAssignmentsThanFromProvidedManager
  )
}

function getBookedEventUuid(
  userBookedLearningItems: { learningItemUuid: string; bookedEventUuid: string }[],
  learningItemUuid: string
): string | undefined {
  return userBookedLearningItems.find((item) => item.learningItemUuid === learningItemUuid)?.bookedEventUuid
}

function getAssignersUuidsOfLearningItemAssignments(learningItem: LearningItem): string[] {
  if (!learningItem.userLearningAssignments?.length) {
    return []
  }

  const uuids = learningItem.userLearningAssignments
    .filter((a) => !!a.assignedByUserUuid)
    .map((a) => a.assignedByUserUuid)

  return uuids as string[]
}
